// src/utils/generateB2BInvoice.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// --- HELPER: Convert Number to Indian Rupees in Words ---
const numberToWords = (num) => {
  const a = [
    "",
    "One ",
    "Two ",
    "Three ",
    "Four ",
    "Five ",
    "Six ",
    "Seven ",
    "Eight ",
    "Nine ",
    "Ten ",
    "Eleven ",
    "Twelve ",
    "Thirteen ",
    "Fourteen ",
    "Fifteen ",
    "Sixteen ",
    "Seventeen ",
    "Eighteen ",
    "Nineteen ",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  if ((num = num.toString()).length > 9) return "Overflow";
  const n = ("000000000" + num)
    .substr(-9)
    .match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return;
  let str = "";

  str +=
    Number(n[1]) !== 0
      ? (a[Number(n[1])] || b[n[1][0]] + " " + a[n[1][1]]) + "Crore "
      : "";
  str +=
    Number(n[2]) !== 0
      ? (a[Number(n[2])] || b[n[2][0]] + " " + a[n[2][1]]) + "Lakh "
      : "";
  str +=
    Number(n[3]) !== 0
      ? (a[Number(n[3])] || b[n[3][0]] + " " + a[n[3][1]]) + "Thousand "
      : "";
  str +=
    Number(n[4]) !== 0
      ? (a[Number(n[4])] || b[n[4][0]] + " " + a[n[4][1]]) + "Hundred "
      : "";
  str +=
    Number(n[5]) !== 0
      ? (str !== "" ? "and " : "") +
        (a[Number(n[5])] || b[n[5][0]] + " " + a[n[5][1]])
      : "";

  return str.trim() + " Only";
};

// --- HELPER: Get State Code ---
const getStateCode = (stateName) => {
  const stateMap = {
    "west bengal": "19",
    maharashtra: "27",
    delhi: "07",
    karnataka: "29",
    "tamil nadu": "33",
    telangana: "36",
    gujarat: "24",
    "uttar pradesh": "09",
  };
  const key = String(stateName).toLowerCase().trim();
  return stateMap[key] || "N/A";
};

export const generateB2BInvoice = (rawData) => {
  if (!rawData) return;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ==========================================
  // HIGH-LEVEL PINCODE & STATE DETECTION
  // ==========================================
  let rawState =
    rawData.place_of_supply ||
    rawData.shipping_state ||
    rawData.state ||
    "West Bengal";
  let isInterState = !String(rawState)
    .toLowerCase()
    .replace(/\s+/g, "")
    .includes("westbengal");

  const addressStringForPin = `${rawData.billing_address || ""} ${rawData.shipping_pincode || ""} ${rawData.pincode || ""}`;
  const pinMatch = addressStringForPin.match(/\b\d{6}\b/);

  if (pinMatch) {
    const pinPrefix = parseInt(pinMatch[0].substring(0, 2), 10);
    if (pinPrefix >= 70 && pinPrefix <= 74) {
      isInterState = false;
      rawState = "West Bengal";
    } else {
      isInterState = true;
      if (String(rawState).toLowerCase().includes("west bengal")) {
        rawState = "Out of State";
      }
    }
  }

  const determinedPlaceOfSupply = rawState;
  const buyerStateCode = getStateCode(determinedPlaceOfSupply);

  // ================= BORDER & WATERMARK =================
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(0.5);
  doc.rect(7, 7, pageWidth - 14, pageHeight - 14);

  doc.setFontSize(28);
  doc.setTextColor(240, 243, 248);
  doc.setFont("helvetica", "bold");
  doc.text("ZYNVENTICS B2B", pageWidth / 2, pageHeight / 2, {
    align: "center",
    baseline: "middle",
    angle: 45,
  });

  // ================= COMPANY CONFIG =================
  const COMPANY = {
    name: "Zynventics Technologies Pvt. Ltd.",
    address1: "Near Youniform world Gobordanga Station road,",
    address2: "Ramnagar, North 24 pargana, 743273",
    state: "West Bengal",
    stateCode: "19",
    gstin: "19AACCZ9658C1ZU",
    pan: "AACCZ9658C",
    cin: "U31900WB2026PTC123456",
    phone: "+91 8902955624",
    email: "info@zynventics.com",
    bank: {
      name: "HDFC Bank",
      accName: "Zynventics Technologies Pvt Ltd",
      accNo: "50200012345678",
      ifsc: "HDFC0001234",
      branch: "Gobordanga",
    },
  };

  // ================= HEADER =================
  let currentY = 22;

  // Left: Company Details
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(COMPANY.name, 14, currentY);

  currentY += 5;
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.setFont("helvetica", "normal");
  doc.text(COMPANY.address1, 14, currentY);
  currentY += 5;
  doc.text(COMPANY.address2, 14, currentY);
  currentY += 5;
  doc.text(`Email: ${COMPANY.email} | Phone: ${COMPANY.phone}`, 14, currentY);
  currentY += 5;
  doc.setFont("helvetica", "bold");
  doc.text(
    `GSTIN: ${COMPANY.gstin}  |  State: ${COMPANY.state} (${COMPANY.stateCode})`,
    14,
    currentY,
  );

  // Right: Invoice Meta (Blue Theme)
  doc.setFontSize(22);
  doc.setTextColor(37, 99, 235);
  doc.setFont("helvetica", "bold");
  doc.text("TAX INVOICE (B2B)", pageWidth - 14, 24, { align: "right" });

  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(
    `Invoice No: ${String(rawData.invoice_number)}`,
    pageWidth - 14,
    32,
    { align: "right" },
  );

  const orderDateStr = new Date(rawData.createdAt || new Date())
    .toLocaleDateString("en-IN")
    .replace(/\//g, "-");
  doc.text(`Date: ${orderDateStr}`, pageWidth - 14, 37, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.text(
    `Place of Supply: ${determinedPlaceOfSupply} (${buyerStateCode})`,
    pageWidth - 14,
    42,
    { align: "right" },
  );
  doc.text("Reverse Charge: No", pageWidth - 14, 47, { align: "right" });

  doc.setDrawColor(200, 200, 200);
  doc.line(14, 52, pageWidth - 14, 52);

  // ================= BUYER DETAILS =================
  let detailsY = 58;
  const col2X = 14;
  const col3X = 114;

  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);

  // Billed To
  doc.setFont("helvetica", "bold");
  doc.text("Billed To (Buyer):", col2X, detailsY);
  doc.setFontSize(9);
  doc.text(
    String(rawData.company_name || rawData.customer_name),
    col2X,
    detailsY + 5,
  );

  doc.setFont("helvetica", "normal");
  if (rawData.company_name) {
    doc.text(`Attn: ${rawData.customer_name}`, col2X, detailsY + 9);
  }

  const addressLines = doc.splitTextToSize(
    String(rawData.billing_address || "N/A"),
    85,
  );
  doc.text(addressLines, col2X, detailsY + 14);
  const addressHeight = addressLines.length * 4;

  let nextLineY = detailsY + 14 + addressHeight;
  doc.text(`Phone: ${rawData.customer_phone || "N/A"}`, col2X, nextLineY);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(37, 99, 235);
  doc.text(
    `Buyer GSTIN: ${rawData.customer_gstin || "URP (Unregistered)"}`,
    col2X,
    nextLineY + 5,
  );
  doc.setTextColor(15, 23, 42);
  doc.text(`State Code: ${buyerStateCode}`, col2X, nextLineY + 9);

  // Shipped To (Now matches Bill To perfectly)
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Shipped To:", col3X, detailsY);
  doc.setFontSize(9);
  doc.text(
    String(rawData.company_name || rawData.customer_name),
    col3X,
    detailsY + 5,
  );
  doc.setFont("helvetica", "normal");
  if (rawData.company_name) {
    doc.text(`Attn: ${rawData.customer_name}`, col3X, detailsY + 9);
  }
  doc.text(addressLines, col3X, detailsY + 14);

  // Added Shipped To Phone & GSTIN
  doc.text(`Phone: ${rawData.customer_phone || "N/A"}`, col3X, nextLineY);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(37, 99, 235);
  doc.text(
    `Buyer GSTIN: ${rawData.customer_gstin || "URP (Unregistered)"}`,
    col3X,
    nextLineY + 5,
  );
  doc.setTextColor(15, 23, 42);
  doc.text(`State Code: ${buyerStateCode}`, col3X, nextLineY + 9);

  const tableStartY = nextLineY + 16;

  // ================= DYNAMIC ITEMS TABLE =================
  const items =
    typeof rawData.items === "string"
      ? JSON.parse(rawData.items)
      : rawData.items;
  const tableRows = [];
  const hsnSummary = {};

  items.forEach((item, index) => {
    const actualGstStored =
      Number(item.igst || 0) + Number(item.cgst || 0) + Number(item.sgst || 0);
    const fallbackGstAmt = Number(item.taxable) * (Number(item.gst_rate) / 100);
    const rowGst = actualGstStored > 0 ? actualGstStored : fallbackGstAmt;

    const hsn = item.hsn || "N/A";
    if (!hsnSummary[hsn]) {
      hsnSummary[hsn] = {
        taxable: 0,
        cgstAmt: 0,
        sgstAmt: 0,
        igstAmt: 0,
        rate: item.gst_rate,
      };
    }
    hsnSummary[hsn].taxable += Number(item.taxable);

    if (isInterState) {
      hsnSummary[hsn].igstAmt += rowGst;
    } else {
      hsnSummary[hsn].cgstAmt += rowGst / 2;
      hsnSummary[hsn].sgstAmt += rowGst / 2;
    }

    const row = [
      index + 1,
      `${item.name}\nSKU: ${item.sku || "N/A"}`,
      hsn,
      item.qty,
      "pcs", // <--- NEW 'PER/UNIT' DATA
      Number(item.unit_price).toFixed(2),
      Number(item.taxable).toFixed(2),
    ];

    if (isInterState) {
      row.push(`${item.gst_rate}%`);
      row.push(rowGst.toFixed(2));
    } else {
      row.push(`${(rowGst / 2).toFixed(2)}\n(${item.gst_rate / 2}%)`);
      row.push(`${(rowGst / 2).toFixed(2)}\n(${item.gst_rate / 2}%)`);
    }

    row.push(item.total);
    tableRows.push(row);
  });

  const tableColumn = [
    "#",
    "Description",
    "HSN/SAC",
    { content: "Qty", styles: { halign: "center" } },
    { content: "Per", styles: { halign: "center" } }, // <--- NEW 'PER/UNIT' HEADER
    { content: "Rate", styles: { halign: "right" } },
    { content: "Taxable", styles: { halign: "right" } },
  ];

  if (isInterState) {
    tableColumn.push(
      { content: "IGST Rate", styles: { halign: "right" } },
      { content: "IGST Amt", styles: { halign: "right" } },
    );
  } else {
    tableColumn.push(
      { content: "CGST", styles: { halign: "right" } },
      { content: "SGST", styles: { halign: "right" } },
    );
  }
  tableColumn.push({ content: "Total", styles: { halign: "right" } });

  autoTable(doc, {
    startY: tableStartY,
    head: [tableColumn],
    body: tableRows,
    theme: "striped",
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: [255, 255, 255],
      fontSize: 8,
    },
    bodyStyles: { fontSize: 8, textColor: [15, 23, 42] },
    columnStyles: {
      0: { cellWidth: 8 },
      3: { halign: "center" },
      4: { halign: "center" }, // Alignment for 'Per'
      5: { halign: "right" },
      6: { halign: "right" },
      7: { halign: "right" },
      8: { halign: "right" },
      9: { halign: "right", fontStyle: "bold" },
    },
  });

  let finalY = doc.lastAutoTable.finalY + 8;
  const labelX = pageWidth - 45;
  const valueX = pageWidth - 14;

  // ================= TOTALS & WORDS =================
  const grandTotal = Math.round(Number(rawData.grand_total));
  const roundedOff = grandTotal - Number(rawData.grand_total);

  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text("Taxable Value:", labelX, finalY, { align: "right" });
  doc.text(
    `Rs. ${Number(rawData.total_taxable_value).toFixed(2)}`,
    valueX,
    finalY,
    { align: "right" },
  );

  let cursorY = finalY;
  const finalTotalGst =
    Number(rawData.total_igst || 0) +
    Number(rawData.total_cgst || 0) +
    Number(rawData.total_sgst || 0);

  if (isInterState) {
    cursorY += 6;
    doc.text("Total IGST:", labelX, cursorY, { align: "right" });
    doc.text(`Rs. ${finalTotalGst.toFixed(2)}`, valueX, cursorY, {
      align: "right",
    });
  } else {
    cursorY += 6;
    doc.text("Total CGST:", labelX, cursorY, { align: "right" });
    doc.text(`Rs. ${(finalTotalGst / 2).toFixed(2)}`, valueX, cursorY, {
      align: "right",
    });
    cursorY += 6;
    doc.text("Total SGST:", labelX, cursorY, { align: "right" });
    doc.text(`Rs. ${(finalTotalGst / 2).toFixed(2)}`, valueX, cursorY, {
      align: "right",
    });
  }

  if (roundedOff !== 0) {
    cursorY += 6;
    doc.text(`Round Off:`, labelX, cursorY, { align: "right" });
    doc.text(
      `${roundedOff > 0 ? "+" : ""}${roundedOff.toFixed(2)}`,
      valueX,
      cursorY,
      { align: "right" },
    );
  }

  // Apple-style Grand Total Box
  cursorY += 8;
  doc.setFillColor(241, 245, 249);
  doc.rect(pageWidth - 80, cursorY - 6, 66, 12, "F");

  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text("Grand Total:", labelX, cursorY + 2, { align: "right" });
  doc.text(
    `Rs. ${grandTotal.toLocaleString("en-IN")}.00`,
    valueX,
    cursorY + 2,
    { align: "right" },
  );

  // Amount in Words (Left Side)
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(71, 85, 105);
  doc.text("Amount Chargeable (in words)", 14, finalY);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 23, 42);
  doc.text(`INR ${numberToWords(grandTotal)}`, 14, finalY + 5);

  // ================= HSN/SAC TAX SUMMARY TABLE =================
  const hsnRows = [];
  let totalSummaryTax = 0;

  Object.keys(hsnSummary).forEach((hsn) => {
    const data = hsnSummary[hsn];
    if (isInterState) {
      totalSummaryTax += data.igstAmt;
      hsnRows.push([
        hsn,
        data.taxable.toFixed(2),
        `${data.rate}%`,
        data.igstAmt.toFixed(2),
        data.igstAmt.toFixed(2),
      ]);
    } else {
      totalSummaryTax += data.cgstAmt + data.sgstAmt;
      hsnRows.push([
        hsn,
        data.taxable.toFixed(2),
        `${data.rate / 2}%`,
        data.cgstAmt.toFixed(2),
        `${data.rate / 2}%`,
        data.sgstAmt.toFixed(2),
        (data.cgstAmt + data.sgstAmt).toFixed(2),
      ]);
    }
  });

  const hsnHead = isInterState
    ? [["HSN/SAC", "Taxable Value", "IGST Rate", "IGST Amt", "Total Tax"]]
    : [
        [
          "HSN/SAC",
          "Taxable Value",
          "CGST Rate",
          "CGST Amt",
          "SGST Rate",
          "SGST Amt",
          "Total Tax",
        ],
      ];

  autoTable(doc, {
    startY: Math.max(cursorY + 12, finalY + 15),
    head: hsnHead,
    body: hsnRows,
    theme: "plain",
    headStyles: {
      fillColor: [241, 245, 249],
      textColor: [15, 23, 42],
      fontSize: 8,
      fontStyle: "bold",
    },
    bodyStyles: { fontSize: 8, textColor: [71, 85, 105] },
    styles: { lineWidth: 0.1, lineColor: [200, 200, 200] },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
      6: { halign: "right" },
    },
  });

  let hsnFinalY = doc.lastAutoTable.finalY;

  // Tax Amount in Words
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("Tax Amount (in words)", 14, hsnFinalY + 6);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 23, 42);
  doc.text(
    `INR ${numberToWords(Math.round(totalSummaryTax))}`,
    14,
    hsnFinalY + 10,
  );

  // ================= DECLARATION & BANK DETAILS =================
  let footerY = hsnFinalY + 18;

  // Added Declaration Section
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("Declaration:", 14, footerY);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(71, 85, 105);
  const declarationText =
    "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.";
  const declarationLines = doc.splitTextToSize(
    declarationText,
    pageWidth / 2 - 10,
  );
  doc.text(declarationLines, 14, footerY + 4);

  // Push bank details slightly down below the declaration
  footerY += 16;

  // Left Side: Bank Details
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 23, 42);
  doc.text("Bank Details:", 14, footerY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(`Account Name: ${COMPANY.bank.accName}`, 14, footerY + 5);
  doc.text(`Bank Name: ${COMPANY.bank.name}`, 14, footerY + 9);
  doc.text(`Account No: ${COMPANY.bank.accNo}`, 14, footerY + 13);
  doc.text(
    `IFSC Code: ${COMPANY.bank.ifsc} | Branch: ${COMPANY.bank.branch}`,
    14,
    footerY + 17,
  );

  // Right Side: Signature
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(`For ${COMPANY.name}`, pageWidth - 14, footerY - 8, {
    align: "right",
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Authorized Signatory", pageWidth - 14, footerY + 17, {
    align: "right",
  });

  doc.line(14, footerY + 22, pageWidth - 14, footerY + 22);

  doc.setFontSize(7);
  doc.setTextColor(100, 116, 139);
  doc.text(
    "This is a Computer Generated Invoice. E. & O.E.",
    pageWidth / 2,
    footerY + 26,
    { align: "center" },
  );

  doc.save(
    `B2B_Invoice_${String(rawData.invoice_number).replace(/\//g, "_")}.pdf`,
  );
};
